import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { adminEspecialidadesQuery, adminDificultadesQuery, adminReactivosPageQuery } from '../../queries/appQueries';
import type { Especialidad as EspecialidadCompartida } from '../../services/especialidadService';
import type { Dificultad as DificultadCompartida } from '../../types/reactivos.types';
import {
  Text,
  Title,
  Group,
  Stack,
  useMantineColorScheme,
  Box,
  Card,
  SimpleGrid,
  ThemeIcon,
  Button,
  Table,
  Modal,
  TextInput,
  Textarea,
  Select,
  Radio,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  Alert,
  Progress,
  List,
  Menu,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import PageTransition from '../../components/animations/PageTransition';
import CountUpNumber from '../../components/animations/CountUpNumber';
import PageSkeleton from '../../components/ui/PageSkeleton';
import AdminPageHeader from '../../components/AdminPageHeader';
import SearchInput from '../../components/ui/SearchInput';
import {
  IconDatabase,
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconFilter,
  IconBrain,
  IconStethoscope,
  IconBook,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconArrowLeft,
  IconUpload,
  IconFileSpreadsheet,
  IconChevronDown,
  IconSettings,
} from '@tabler/icons-react';
import reactivosService, { ReactivosPage } from '../../services/reactivosService';
import temaService from '../../services/temaService';
import type { ReactivoDTO, ReactivoCreateUpdateDTO, Tema } from '../../types/reactivos.types';

const QuestionDatabasePage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();

  // Paleta sobria unificada con Dashboard/Estadísticas (negro/beige, sin blur ni colores).
  const cardStyle: React.CSSProperties = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
  const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#8c8478';
  const dark = colorScheme === 'dark';
  // Colores de texto para las columnas categóricas (sin badges).
  const colEspecialidad = dark ? '#8aa9d6' : '#5c7bb0'; // azul apagado
  const colTema = dark ? '#86b8a6' : '#4f8a74';         // verde/teal apagado
  const difColor = (nombre?: string) => {
    const n = (nombre || '').toLowerCase();
    if (/(fácil|facil|básic|basic)/.test(n)) return dark ? '#86b8a6' : '#4f8a74';
    if (/(inter|medio|moder)/.test(n)) return dark ? '#cdb487' : '#a07d3c';
    if (/(difícil|dificil|alto|avanz)/.test(n)) return dark ? '#d29ba0' : '#b5565e';
    return mutedColor;
  };
  // Primeras palabras de la pregunta (el resto se ve en el tooltip al pasar el cursor).
  const resumenPregunta = (texto: string, palabras = 4) => {
    const w = texto.trim().split(/\s+/);
    return w.length <= palabras ? texto : w.slice(0, palabras).join(' ') + '…';
  };

  const queryClient = useQueryClient();

  const [temas, setTemas] = useState<Tema[]>([]);

  // Especialidades y dificultades vienen del caché compartido (prefetch al login admin).
  const especialidadesResult = useQuery(adminEspecialidadesQuery());
  const dificultadesResult = useQuery(adminDificultadesQuery());
  const especialidades = (especialidadesResult.data as EspecialidadCompartida[] | undefined) ?? [];
  const dificultades = (dificultadesResult.data as DificultadCompartida[] | undefined) ?? [];
  
  // Estados para modales y vistas
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit' | 'detail'>('list');
  const [selectedReactivo, setSelectedReactivo] = useState<ReactivoDTO | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reactivoToDelete, setReactivoToDelete] = useState<ReactivoDTO | null>(null);

  // Estados para importación CSV
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResultado, setCsvResultado] = useState<{ insertados: number; omitidos: number; errores: string[] } | null>(null);
  
  // Estados para filtros
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [especialidadFilter, setEspecialidadFilter] = useState<string | null>(null);
  const [temaFilter, setTemaFilter] = useState<string | null>(null);
  const [dificultadFilter, setDificultadFilter] = useState<string | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Búsqueda con debounce para no pegarle al backend en cada tecla.
  const [debouncedSearch] = useDebouncedValue(searchQuery, 350);

  // Banco de reactivos: paginado server-side (filtros + búsqueda en el backend).
  // Prefetcheado al login admin → abre al instante; keepPreviousData evita parpadeo al paginar.
  const reactivosResult = useQuery(
    adminReactivosPageQuery({
      page: activePage - 1,
      size: itemsPerPage,
      especialidadId: especialidadFilter ? Number(especialidadFilter) : null,
      temaId: temaFilter ? Number(temaFilter) : null,
      dificultadId: dificultadFilter ? Number(dificultadFilter) : null,
      q: debouncedSearch,
    })
  );
  const reactivosPage = reactivosResult.data as ReactivosPage | undefined;
  const reactivos = reactivosPage?.content ?? [];
  const totalElements = reactivosPage?.totalElements ?? 0;
  const totalPages = reactivosPage?.totalPages ?? 0;
  const loading = reactivosResult.isLoading;
  const error = reactivosResult.error ? 'Error al cargar los datos' : null;

  const hayFiltrosActivos = !!(especialidadFilter || temaFilter || dificultadFilter || debouncedSearch.trim());

  // "Total Reactivos" = total del banco sin filtros. Se toma del totalElements de la
  // query principal cuando no hay filtros y se conserva al filtrar.
  const [totalReactivos, setTotalReactivos] = useState(0);
  useEffect(() => {
    if (!hayFiltrosActivos && totalElements > 0) setTotalReactivos(totalElements);
  }, [hayFiltrosActivos, totalElements]);

  // Estados para formulario
  const [formData, setFormData] = useState<ReactivoCreateUpdateDTO>({
    pregunta: '',
    respuestaA: '',
    respuestaB: '',
    respuestaC: '',
    respuestaD: '',
    respuestaCorrecta: '',
    retroalimentacion: '',
    claveId: 0,
    dificultadId: 0,
    especialidadId: 0,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Cargar temas cuando cambia la especialidad en el formulario
  useEffect(() => {
    if (formData.especialidadId > 0) {
      loadTemasByEspecialidad(formData.especialidadId);
    }
  }, [formData.especialidadId]);

  // Volver a la primera página cuando cambian los filtros o el tamaño de página.
  useEffect(() => {
    setActivePage(1);
  }, [especialidadFilter, temaFilter, dificultadFilter, debouncedSearch, itemsPerPage]);

  // Refrescar el banco cacheado (tras crear/editar/eliminar/importar).
  const refrescarReactivos = () =>
    queryClient.invalidateQueries({ queryKey: ['adminReactivos'] });

  const loadTemasByEspecialidad = async (especialidadId: number) => {
    try {
      const temasData = await temaService.getTemasByEspecialidad(especialidadId);
      setTemas(temasData);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'No se pudieron cargar los temas',
        color: 'red',
      });
    }
  };

  const handleClearFilters = () => {
    setEspecialidadFilter(null);
    setTemaFilter(null);
    setDificultadFilter(null);
    setSearchQuery('');
    setTemas([]);
  };

  const handleEspecialidadFilterChange = async (value: string | null) => {
    setEspecialidadFilter(value);
    setTemaFilter(null);
    if (value && Number(value) > 0) {
      await loadTemasByEspecialidad(Number(value));
    } else {
      setTemas([]);
    }
  };

  const handleNewReactivo = () => {
    setFormData({
      pregunta: '',
      respuestaA: '',
      respuestaB: '',
      respuestaC: '',
      respuestaD: '',
      respuestaCorrecta: '',
      retroalimentacion: '',
      claveId: 0,
      dificultadId: 0,
      especialidadId: 0,
    });
    setFormErrors({});
    setViewMode('create');
  };

  const handleEditReactivo = (reactivo: ReactivoDTO) => {
    setSelectedReactivo(reactivo);
    setFormData({
      pregunta: reactivo.pregunta,
      respuestaA: reactivo.respuestaA,
      respuestaB: reactivo.respuestaB,
      respuestaC: reactivo.respuestaC,
      respuestaD: reactivo.respuestaD,
      respuestaCorrecta: reactivo.respuestaCorrecta,
      retroalimentacion: reactivo.retroalimentacion || '',
      claveId: reactivo.claveId,
      dificultadId: reactivo.dificultadId,
      especialidadId: reactivo.especialidadId,
    });
    setFormErrors({});
    setViewMode('edit');
  };

  const handleViewReactivo = (reactivo: ReactivoDTO) => {
    setSelectedReactivo(reactivo);
    setViewMode('detail');
  };

  const handleDeleteClick = (reactivo: ReactivoDTO) => {
    setReactivoToDelete(reactivo);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reactivoToDelete) return;
    
    try {
      await reactivosService.delete(reactivoToDelete.id);
      notifications.show({
        title: 'Éxito',
        message: 'Reactivo eliminado exitosamente',
        color: 'green',
        icon: <IconCheck />,
      });
      setDeleteModalOpen(false);
      setReactivoToDelete(null);
      refrescarReactivos();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Error al eliminar reactivo',
        color: 'red',
        icon: <IconX />,
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.pregunta || formData.pregunta.length < 10) {
      errors.pregunta = 'La pregunta debe tener al menos 10 caracteres';
    }
    if (!formData.respuestaA || formData.respuestaA.length < 2) {
      errors.respuestaA = 'La respuesta A es obligatoria';
    }
    if (!formData.respuestaB || formData.respuestaB.length < 2) {
      errors.respuestaB = 'La respuesta B es obligatoria';
    }
    if (!formData.respuestaC || formData.respuestaC.length < 2) {
      errors.respuestaC = 'La respuesta C es obligatoria';
    }
    if (!formData.respuestaD || formData.respuestaD.length < 2) {
      errors.respuestaD = 'La respuesta D es obligatoria';
    }
    if (!formData.respuestaCorrecta || !['a', 'b', 'c', 'd'].includes(formData.respuestaCorrecta)) {
      errors.respuestaCorrecta = 'Debes seleccionar la respuesta correcta';
    }
    if (!formData.especialidadId || formData.especialidadId <= 0) {
      errors.especialidadId = 'Debes seleccionar una especialidad';
    }
    if (!formData.claveId || formData.claveId <= 0) {
      errors.claveId = 'Debes seleccionar un tema';
    }
    if (!formData.dificultadId || formData.dificultadId <= 0) {
      errors.dificultadId = 'Debes seleccionar la dificultad';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (viewMode === 'edit' && selectedReactivo) {
        await reactivosService.update(selectedReactivo.id, formData);
        notifications.show({
          title: 'Éxito',
          message: 'Reactivo actualizado exitosamente',
          color: 'green',
          icon: <IconCheck />,
        });
      } else {
        await reactivosService.create(formData);
        notifications.show({
          title: 'Éxito',
          message: 'Reactivo creado exitosamente',
          color: 'green',
          icon: <IconCheck />,
        });
      }
      setViewMode('list');
      refrescarReactivos();
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: `${err.response?.data?.error || 'Error al guardar reactivo'}`,
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Importar CSV
  const handleImportarCSV = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvResultado(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/admin/reactivos/importar-csv', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al importar');
      setCsvResultado(data);
      notifications.show({ title: 'Importación exitosa', message: `${data.insertados} reactivos importados`, color: 'green' });
      refrescarReactivos();
    } catch (e: any) {
      notifications.show({ title: 'Error', message: e.message, color: 'red' });
    } finally {
      setCsvUploading(false);
    }
  };

  // La paginación la resuelve el servidor: `reactivos` ya es la página actual y
  // `totalPages`/`totalElements` vienen de la respuesta.

  // Renderizar vista de lista
  const renderListView = () => {
    if (loading) {
      return <PageSkeleton variant="grid" count={4} />;
    }

    const statsCards = [
      { icon: IconDatabase, value: totalReactivos, label: 'Total Reactivos', duration: 2500 },
      { icon: IconBrain, value: especialidades.length, label: 'Especialidades', duration: 2000 },
      { icon: IconStethoscope, value: dificultades.length, label: 'Niveles de Dificultad', duration: 2000 },
      { icon: IconBook, value: totalElements, label: 'Filtrados', duration: 2000 },
    ];

    // Filtro desplegable estilo Videos: botón que oculta/despliega las opciones al picarlo.
    const filterMenu = (opts: {
      label: string;
      selectedId: string | null;
      items: Array<{ id: number; nombre: string }>;
      onSelect: (id: string | null) => void;
      disabled?: boolean;
    }) => {
      const selected = opts.items.find((i) => i.id.toString() === opts.selectedId);
      return (
        <Menu shadow="md" width={250} position="bottom-start" withinPortal disabled={opts.disabled}>
          <Menu.Target>
            <Button
              variant={opts.selectedId ? 'light' : 'default'}
              color="gray"
              radius="md"
              disabled={opts.disabled}
              leftSection={<IconFilter size={16} />}
              rightSection={<IconChevronDown size={14} />}
            >
              {selected ? selected.nombre : opts.label}
            </Button>
          </Menu.Target>
          <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
            <Menu.Item
              leftSection={!opts.selectedId ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
              onClick={() => opts.onSelect(null)}
            >
              {opts.label} (todas)
            </Menu.Item>
            <Menu.Divider />
            {opts.items.map((it) => {
              const sel = opts.selectedId === it.id.toString();
              return (
                <Menu.Item
                  key={it.id}
                  onClick={() => opts.onSelect(it.id.toString())}
                  rightSection={sel ? <IconCheck size={14} /> : null}
                >
                  <Text size="sm" fw={sel ? 700 : 500}>{it.nombre}</Text>
                </Menu.Item>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      );
    };

    return (
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <AdminPageHeader
              title="Banco de Reactivos"
              subtitle="Gestiona y organiza las preguntas del simulador ENARM"
            />
            <Group>
              <Button
                leftSection={<IconUpload size={16} />}
                variant="default"
                onClick={() => { setCsvFile(null); setCsvResultado(null); setCsvModalOpen(true); }}
                styles={{
                  root: {
                    borderColor: textColor,
                    color: textColor,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Importar CSV
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleNewReactivo}
                styles={{
                  root: {
                    backgroundColor: textColor,
                    color: colorScheme === 'dark' ? '#1e293b' : '#f7f3ee',
                  },
                }}
              >
                Crear Nuevo Reactivo
              </Button>
            </Group>
          </Group>

          {/* Stats Cards (paleta sobria, icono negro arriba-izquierda) */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
            {statsCards.map((s) => (
              <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
                <Stack gap="sm" align="stretch">
                  <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                  <Box style={{ minWidth: 0 }}>
                    <CountUpNumber
                      value={s.value}
                      duration={s.duration}
                      style={{
                        color: textColor,
                        fontSize: '1.6rem',
                        fontWeight: 700,
                        fontFamily: 'Space Grotesk, Inter, sans-serif',
                        textAlign: 'left',
                      }}
                    />
                    <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                      {s.label}
                    </Text>
                  </Box>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          {/* Búsqueda + filtros desplegables (sin card, estilo Videos) */}
          <Group gap="sm" align="center" wrap="wrap">
            <SearchInput
              placeholder="Buscar en preguntas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            {filterMenu({
              label: 'Especialidad',
              selectedId: especialidadFilter,
              items: especialidades,
              onSelect: handleEspecialidadFilterChange,
            })}
            {filterMenu({
              label: 'Tema',
              selectedId: temaFilter,
              items: temas,
              onSelect: setTemaFilter,
              disabled: !especialidadFilter,
            })}
            {filterMenu({
              label: 'Dificultad',
              selectedId: dificultadFilter,
              items: dificultades,
              onSelect: setDificultadFilter,
            })}
            {(especialidadFilter || temaFilter || dificultadFilter || searchQuery) && (
              <Button variant="subtle" color="gray" onClick={handleClearFilters}>
                Limpiar
              </Button>
            )}
          </Group>

          {/* Table */}
          {error ? (
            <Alert icon={<IconAlertCircle />} title="Error" color="red">
              {error}
            </Alert>
          ) : reactivos.length === 0 ? (
            <Card withBorder radius="lg" p="xl" style={cardStyle}>
              <Center>
                <Stack align="center" gap="md">
                  <IconDatabase size={64} style={{ opacity: 0.3, color: mutedColor }} />
                  <Title order={4} style={{ color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                    {hayFiltrosActivos ? 'Sin resultados' : 'No hay reactivos creados'}
                  </Title>
                  <Text style={{ color: mutedColor }}>
                    {hayFiltrosActivos
                      ? 'Ningún reactivo coincide con los filtros aplicados.'
                      : '¡Crea el primero haciendo clic en "Crear Nuevo Reactivo"!'}
                  </Text>
                </Stack>
              </Center>
            </Card>
          ) : (
            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Table.ScrollContainer minWidth={760}>
                <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Pregunta</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Especialidad</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Tema</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Dificultad</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 90, textAlign: 'right' }}>Acciones</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {reactivos.map((reactivo) => (
                      <Table.Tr key={reactivo.id}>
                        <Table.Td>
                          <Tooltip label={reactivo.pregunta} multiline w={420} withArrow position="top-start">
                            <Text size="xs" fw={500} ta="left" style={{ color: textColor, cursor: 'help', whiteSpace: 'nowrap' }}>
                              {resumenPregunta(reactivo.pregunta)}
                            </Text>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={reactivo.especialidadNombre} withArrow>
                            <Text size="xs" fw={500} ta="left" style={{ color: colEspecialidad, cursor: 'help' }}>
                              {reactivo.especialidadNombre}
                            </Text>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={reactivo.claveNombre} withArrow>
                            <Text size="xs" fw={500} ta="left" style={{ color: colTema, cursor: 'help' }}>
                              {reactivo.claveNombre}
                            </Text>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500} ta="left" style={{ color: difColor(reactivo.dificultadNombre) }}>
                            {reactivo.dificultadNombre}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Menu shadow="md" width={170} position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray" size="md" aria-label="Acciones">
                                <IconSettings size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item leftSection={<IconEye size={14} />} onClick={() => handleViewReactivo(reactivo)}>
                                Ver
                              </Menu.Item>
                              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleEditReactivo(reactivo)}>
                                Editar
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => handleDeleteClick(reactivo)}
                              >
                                Eliminar
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              {/* Footer: "Mostrando X de Y" + tamaño de página (izq) y paginación (der) */}
              <Group justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
                <Group gap="xs" align="center">
                  <Text size="xs" style={{ color: mutedColor }}>
                    Mostrando {reactivos.length} de {totalElements.toLocaleString('es-MX')}
                  </Text>
                  <Select
                    size="xs"
                    w={92}
                    data={['10', '20', '50', '100']}
                    value={String(itemsPerPage)}
                    onChange={(v) => v && setItemsPerPage(Number(v))}
                    allowDeselect={false}
                    aria-label="Reactivos por página"
                  />
                </Group>
                {totalPages > 1 && (
                  <Pagination
                    value={activePage}
                    onChange={setActivePage}
                    total={totalPages}
                    color="gray"
                    size="sm"
                  />
                )}
              </Group>
            </Card>
          )}
        </Stack>
      </Box>
    );
  };

  // Renderizar formulario (crear/editar)
  const renderFormView = () => (
    <Box
      style={{
        minHeight: '100vh',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        padding: '25.6px',
      }}
    >
      <Stack gap="lg">
        <Group>
          <Button leftSection={<IconArrowLeft size={16} />} variant="light" onClick={() => setViewMode('list')}>
            Volver a Lista
          </Button>
        </Group>

        <Title order={2} style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
          {viewMode === 'edit' ? 'Editar Reactivo' : 'Crear Nuevo Reactivo'}
        </Title>

        <Card radius="xl" p="xl" style={{
          backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(40px) saturate(200%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
        }}>
          <Stack gap="lg">
            {/* Pregunta */}
            <div>
              <Title order={5} mb="xs" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                PREGUNTA
              </Title>
              <Textarea
                placeholder="Escribe aquí la pregunta del reactivo... (Mínimo 10 caracteres)"
                value={formData.pregunta}
                onChange={(e) => setFormData({ ...formData, pregunta: e.currentTarget.value })}
                minRows={3}
                error={formErrors.pregunta}
                required
              />
            </div>

            {/* Opciones de Respuesta */}
            <div>
              <Title order={5} mb="xs" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                OPCIONES DE RESPUESTA
              </Title>
              <Stack gap="sm">
                <TextInput
                  label="Respuesta A *"
                  placeholder="Primera opción..."
                  value={formData.respuestaA}
                  onChange={(e) => setFormData({ ...formData, respuestaA: e.currentTarget.value })}
                  error={formErrors.respuestaA}
                  required
                />
                <TextInput
                  label="Respuesta B *"
                  placeholder="Segunda opción..."
                  value={formData.respuestaB}
                  onChange={(e) => setFormData({ ...formData, respuestaB: e.currentTarget.value })}
                  error={formErrors.respuestaB}
                  required
                />
                <TextInput
                  label="Respuesta C *"
                  placeholder="Tercera opción..."
                  value={formData.respuestaC}
                  onChange={(e) => setFormData({ ...formData, respuestaC: e.currentTarget.value })}
                  error={formErrors.respuestaC}
                  required
                />
                <TextInput
                  label="Respuesta D *"
                  placeholder="Cuarta opción..."
                  value={formData.respuestaD}
                  onChange={(e) => setFormData({ ...formData, respuestaD: e.currentTarget.value })}
                  error={formErrors.respuestaD}
                  required
                />
                
                <div>
                  <Text size="sm" fw={500} mb="xs" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                    Respuesta Correcta: *
                  </Text>
                  <Radio.Group
                    value={formData.respuestaCorrecta}
                    onChange={(value) => setFormData({ ...formData, respuestaCorrecta: value })}
                    error={formErrors.respuestaCorrecta}
                  >
                    <Group>
                      <Radio value="a" label="A" />
                      <Radio value="b" label="B" />
                      <Radio value="c" label="C" />
                      <Radio value="d" label="D" />
                    </Group>
                  </Radio.Group>
                </div>
              </Stack>
            </div>

            {/* Retroalimentación */}
            <div>
              <Title order={5} mb="xs" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                RETROALIMENTACIÓN
              </Title>
              <Textarea
                placeholder="Explica por qué esta es la respuesta correcta... (Opcional)"
                value={formData.retroalimentacion}
                onChange={(e) => setFormData({ ...formData, retroalimentacion: e.currentTarget.value })}
                minRows={3}
              />
            </div>

            {/* Clasificación */}
            <div>
              <Title order={5} mb="xs" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                CLASIFICACIÓN DEL REACTIVO
              </Title>
              <Stack gap="sm">
                <Select
                  label="Especialidad *"
                  placeholder="Seleccionar especialidad..."
                  value={formData.especialidadId > 0 ? formData.especialidadId.toString() : ''}
                  onChange={(value) => {
                    setFormData({ ...formData, especialidadId: Number(value) || 0, claveId: 0 });
                    setTemas([]);
                  }}
                  data={especialidades.map(esp => ({ value: esp.id.toString(), label: esp.nombre }))}
                  error={formErrors.especialidadId}
                  required
                  searchable
                />
                <Select
                  label="Tema *"
                  placeholder={formData.especialidadId > 0 ? "Seleccionar tema..." : "Primero selecciona una especialidad..."}
                  value={formData.claveId > 0 ? formData.claveId.toString() : ''}
                  onChange={(value) => setFormData({ ...formData, claveId: Number(value) || 0 })}
                  data={temas.map(tema => ({ value: tema.id.toString(), label: tema.nombre }))}
                  error={formErrors.claveId}
                  disabled={!formData.especialidadId || temas.length === 0}
                  required
                  searchable
                />
                <Select
                  label="Dificultad *"
                  placeholder="Seleccionar dificultad..."
                  value={formData.dificultadId > 0 ? formData.dificultadId.toString() : ''}
                  onChange={(value) => setFormData({ ...formData, dificultadId: Number(value) || 0 })}
                  data={dificultades.map(dif => ({ value: dif.id.toString(), label: dif.nombre }))}
                  error={formErrors.dificultadId}
                  required
                  searchable
                />
              </Stack>
            </div>

            {/* Botones */}
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setViewMode('list')} disabled={submitting}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} loading={submitting}>
                {viewMode === 'edit' ? 'Actualizar Reactivo' : 'Guardar Reactivo'}
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );

  // Renderizar vista de detalle
  const renderDetailView = () => {
    if (!selectedReactivo) return null;

    return (
      <Box
        style={{
          minHeight: '100vh',
          background: colorScheme === 'dark'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          padding: '25.6px',
        }}
      >
        <Stack gap="lg">
          <Group justify="space-between">
            <Button leftSection={<IconArrowLeft size={16} />} variant="light" onClick={() => setViewMode('list')}>
              Volver a Lista
            </Button>
            <Group>
              <Button leftSection={<IconEdit size={16} />} onClick={() => handleEditReactivo(selectedReactivo)}>
                Editar
              </Button>
              <Button leftSection={<IconTrash size={16} />} color="red" onClick={() => handleDeleteClick(selectedReactivo)}>
                Eliminar
              </Button>
            </Group>
          </Group>

          <Title order={2} style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>DETALLE DEL REACTIVO #{selectedReactivo.id}
          </Title>

          {/* Pregunta */}
          <Card radius="xl" p="xl" style={{
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
          }}>
            <Title order={5} mb="md" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
              PREGUNTA
            </Title>
            <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155', fontSize: '1.1rem' }}>
              {selectedReactivo.pregunta}
            </Text>
          </Card>

          {/* Opciones de Respuesta */}
          <Card radius="xl" p="xl" style={{
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
          }}>
            <Title order={5} mb="md" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
              OPCIONES DE RESPUESTA
            </Title>
            <Stack gap="sm">
              {[
                { letter: 'A', text: selectedReactivo.respuestaA },
                { letter: 'B', text: selectedReactivo.respuestaB },
                { letter: 'C', text: selectedReactivo.respuestaC },
                { letter: 'D', text: selectedReactivo.respuestaD },
              ].map(option => (
                <Group key={option.letter}>
                  {selectedReactivo.respuestaCorrecta.toUpperCase() === option.letter ? (
                    <ThemeIcon color="green" size="md" radius="xl">
                      <IconCheck size={16} />
                    </ThemeIcon>
                  ) : (
                    <ThemeIcon color="red" size="md" radius="xl">
                      <IconX size={16} />
                    </ThemeIcon>
                  )}
                  <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                    {option.letter}) {option.text}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Card>

          {/* Retroalimentación */}
          {selectedReactivo.retroalimentacion && (
            <Card radius="xl" p="xl" style={{
              backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
            }}>
              <Title order={5} mb="md" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                RETROALIMENTACIÓN
              </Title>
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                {selectedReactivo.retroalimentacion}
              </Text>
            </Card>
          )}

          {/* Clasificación */}
          <Card radius="xl" p="xl" style={{
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
          }}>
            <Title order={5} mb="md" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
              CLASIFICACIÓN
            </Title>
            <Stack gap="xs">
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>Especialidad:</strong> {selectedReactivo.especialidadNombre}
              </Text>
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>Tema:</strong> {selectedReactivo.claveNombre}
              </Text>
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>Dificultad:</strong> {selectedReactivo.dificultadNombre}
              </Text>
            </Stack>
          </Card>

          {/* Información Adicional */}
          <Card radius="xl" p="xl" style={{
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
          }}>
            <Title order={5} mb="md" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
              INFORMACIÓN ADICIONAL
            </Title>
            <Stack gap="xs">
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>Creado por:</strong> {selectedReactivo.usuarioUsername || 'N/A'}
              </Text>
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>Fecha creación:</strong> {selectedReactivo.fechaCreacion ? new Date(selectedReactivo.fechaCreacion).toLocaleString('es-MX') : '—'}
              </Text>
              <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
                <strong>ID del reactivo:</strong> {selectedReactivo.id}
              </Text>
            </Stack>
          </Card>
        </Stack>
      </Box>
    );
  };

  return (
    <PageTransition type="medical" duration={800}>
      <>
        {viewMode === 'list' && renderListView()}
        {(viewMode === 'create' || viewMode === 'edit') && renderFormView()}
        {viewMode === 'detail' && renderDetailView()}

        {/* Modal de Importación CSV */}
        <Modal
          opened={csvModalOpen}
          onClose={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}
          title="Importar Reactivos desde CSV"
          size="lg"
          centered
          styles={{
            content: {
              backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(40px) saturate(200%)',
            }
          }}
        >
          <Stack gap="md">
            <Alert color="blue" icon={<IconFileSpreadsheet size={16} />}>
              <Text size="sm" fw={600} mb={4}>Formato esperado del CSV:</Text>
              <Text size="xs" style={{ fontFamily: 'monospace' }}>
                Pregunta, A, B, C, D, Respuesta Correcta, Retroalimentación, CLAVE, SUBCLAVE, GRADO DE DIFICULTAD
              </Text>
              <Text size="xs" mt={4} c="dimmed">
                CLAVE: 1=Pediatría, 2=Medicina Interna, 3=Cirugía General, 4=Ginecología y Obstetricia, 5=Urgencias<br/>
                SUBCLAVE: ID del tema (1–28) · GRADO: 1=Fácil, 2=Intermedio, 3=Difícil
              </Text>
            </Alert>

            {!csvResultado ? (
              <>
                <Box
                  component="label"
                  htmlFor="csv-upload"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 100,
                    border: `2px dashed ${colorScheme === 'dark' ? '#475569' : '#cbd5e1'}`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    gap: 8,
                    background: colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <IconUpload size={32} color="gray" />
                  <Text size="sm" fw={500}>Haz clic para seleccionar un archivo CSV</Text>
                  <Text size="xs" c="dimmed">Solo archivos .csv</Text>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    title="Seleccionar archivo CSV"
                    aria-label="Seleccionar archivo CSV"
                    className="csv-hidden-input"
                    onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                    disabled={csvUploading}
                  />
                </Box>

                {csvFile && (
                  <Alert color="green" icon={<IconCheck size={16} />}>
                    Archivo seleccionado: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
                  </Alert>
                )}

                {csvUploading && <Progress value={100} animated />}

                <Group justify="flex-end">
                  <Button variant="light" onClick={() => setCsvModalOpen(false)} disabled={csvUploading}>
                    Cancelar
                  </Button>
                  <Button
                    leftSection={<IconUpload size={16} />}
                    onClick={handleImportarCSV}
                    disabled={!csvFile}
                    loading={csvUploading}
                    color="green"
                  >
                    Importar
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Alert color={csvResultado.errores.length > 0 ? 'yellow' : 'green'} icon={<IconCheck size={16} />}>
                  <Text fw={600}>Importación completada</Text>
                  <Text size="sm">{csvResultado.insertados} reactivos importados correctamente</Text>
                  <Text size="sm">{csvResultado.omitidos} filas omitidas (vacías o sin mapeo)</Text>
                </Alert>
                {csvResultado.errores.length > 0 && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} title="Errores encontrados:">
                    <List size="xs">
                      {csvResultado.errores.map((e, i) => <List.Item key={i}>{e}</List.Item>)}
                    </List>
                  </Alert>
                )}
                <Group justify="flex-end">
                  <Button onClick={() => { setCsvModalOpen(false); setCsvFile(null); setCsvResultado(null); }}>
                    Cerrar
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Modal>

        {/* Modal de Eliminación */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="¿Eliminar este reactivo?"
          centered
          styles={{
            content: {
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(40px) saturate(200%)',
            }
          }}
        >
          <Stack gap="md">
            <Text style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#334155' }}>
              Esta acción no se puede deshacer.
            </Text>
            {reactivoToDelete && (
              <Text size="sm" style={{ color: colorScheme === 'dark' ? '#cbd5e1' : '#64748b' }}>
                <strong>Pregunta:</strong> "{reactivoToDelete.pregunta.substring(0, 100)}..."
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setDeleteModalOpen(false)}>
                Cancelar
              </Button>
              <Button color="red" onClick={handleDeleteConfirm}>
                Eliminar
              </Button>
            </Group>
          </Stack>
        </Modal>
      </>
    </PageTransition>
  );
};

export default QuestionDatabasePage;
